import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function GenerateInvoiceModal({ onClose, onSuccess }) {
  const [workTicketNumber, setWorkTicketNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicateDetails, setDuplicateDetails] = useState(null);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [deliveryNoteId, setDeliveryNoteId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSubmit = async (e, confirmDuplicate = false) => {
    e.preventDefault();
    
    if (!workTicketNumber || isNaN(workTicketNumber)) {
      setError('Please enter a valid Work Ticket Number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/invoices/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          workTicketNumber: parseInt(workTicketNumber),
          confirmDuplicate: confirmDuplicate
        })
      });

      const data = await res.json();

      // Handle duplicate confirmation required (409 status)
      if (res.status === 409 && data.requiresConfirmation) {
        setExistingInvoice(data.existingInvoice);
        setDeliveryNoteId(data.deliveryNoteId);
        setShowConfirmation(true);
        setLoading(false);
        return;
      }

      // Handle duplicate item codes error
      if (res.status === 400 && data.error === 'DUPLICATE_ITEM_CODES') {
        setDuplicateDetails(data.details);
        setLoading(false);
        return;
      }

      // Handle missing EST line error
      if (res.status === 400 && data.error === 'MISSING_EST_LINE') {
        setError(data.message + '\n\nPlease create the invoice manually in Xero or contact the Planning Department.');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate invoice');
      }

      alert('Invoice generated successfully!');
      onSuccess();
    } catch (err) {
      console.error('Error generating invoice:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDuplicate = (e) => {
    setShowConfirmation(false);
    setExistingInvoice(null);
    setDeliveryNoteId(null);
    handleSubmit(e, true);
  };

  const handleCancelDuplicate = () => {
    setShowConfirmation(false);
    setExistingInvoice(null);
    setDeliveryNoteId(null);
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '32px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: 700, color: '#1976d2' }}>
          Generate Invoice
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 600,
              color: '#333'
            }}>
              Work Ticket Number
            </label>
            <input
              type="text"
              value={workTicketNumber}
              onChange={(e) => setWorkTicketNumber(e.target.value)}
              placeholder="Enter Work Ticket Number (e.g., 64244)"
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ 
              marginTop: '8px', 
              fontSize: '12px', 
              color: '#666',
              lineHeight: '1.5'
            }}>
              Enter the Work Ticket number from the Trans No column in QuickEasy. This will fetch the Work Ticket and Delivery Note data to create an invoice.
            </div>
          </div>

          {error && (
            <div style={{
              padding: '12px',
              background: '#ffebee',
              color: '#c62828',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {duplicateDetails && (
            <div style={{
              padding: '16px',
              background: '#fff3cd',
              border: '2px solid #ff9800',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', color: '#856404', fontSize: '16px' }}>
                ⚠️ Duplicate Item Codes Detected
              </div>
              <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px' }}>
                This Work Ticket contains the same item code with different descriptions:
              </div>
              <div style={{
                padding: '12px',
                background: '#fff',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '13px',
                fontFamily: 'monospace',
                whiteSpace: 'pre-line',
                color: '#333'
              }}>
                {duplicateDetails}
              </div>
              <div style={{ fontSize: '14px', color: '#856404', marginBottom: '8px', fontWeight: 600 }}>
                Automated invoice generation is not available for this Work Ticket.
              </div>
              <div style={{ fontSize: '13px', color: '#856404', lineHeight: '1.6' }}>
                <strong>Options:</strong>
                <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                  <li>Create the invoice manually in Xero, OR</li>
                  <li>Contact the Planning Department to update the Work Ticket with unique item codes (e.g., RRW01-0512-25, RRW01-0512-26)</li>
                </ul>
              </div>
            </div>
          )}

          {showConfirmation && existingInvoice && (
            <div style={{
              padding: '16px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', color: '#856404' }}>
                ⚠️ Delivery Note Already Invoiced
              </div>
              <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px' }}>
                An invoice already exists for Delivery Note {deliveryNoteId}:
              </div>
              <div style={{
                padding: '12px',
                background: '#fff',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '13px'
              }}>
                <div><strong>Invoice:</strong> {existingInvoice.invoiceNumber}</div>
                <div><strong>Customer:</strong> {existingInvoice.customerName}</div>
                <div><strong>Total:</strong> R{existingInvoice.total.toFixed(2)}</div>
                <div><strong>Status:</strong> {existingInvoice.status}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  Created: {new Date(existingInvoice.createdAt).toLocaleString()}
                </div>
              </div>
              <div style={{ fontSize: '13px', color: '#856404', fontStyle: 'italic', lineHeight: '1.5' }}>
                <strong>Note:</strong> Each Delivery Note should only be invoiced once. If you need to invoice a new batch for WT-{workTicketNumber}, create a new Delivery Note in QuickEasy first, then generate the invoice using that new DN.
              </div>
              <div style={{ fontSize: '12px', color: '#d32f2f', marginTop: '8px', fontStyle: 'italic' }}>
                Only click "Confirm & Recreate" if you need to recreate this specific invoice (not recommended).
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={showConfirmation ? handleCancelDuplicate : onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            {showConfirmation ? (
              <button
                type="button"
                onClick={handleConfirmDuplicate}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  background: loading ? '#ccc' : '#ff9800',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Recreating...' : 'Confirm & Recreate'}
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  background: loading ? '#ccc' : '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Generating...' : 'Generate Invoice'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default GenerateInvoiceModal;
